import { ICellRendererParams } from "ag-grid-community";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, Loader } from "rsuite";
import { useAuthenticatedFileBlob } from "../utils/useAuthenticatedFileBlob";
import "./GridInlineThumbnailCell.scss";

// ---------------------------------------------------------------------------

const ThumbnailImage = ({
  id,
  onClick,
}: {
  id: string;
  onClick: () => void;
}) => {
  const { blobUrl, isLoading } = useAuthenticatedFileBlob(id);

  if (isLoading) return <div className="grid-thumbnail-skeleton" />;

  if (!blobUrl) return null;

  return (
    <img
      src={blobUrl}
      alt=""
      className="grid-thumbnail-img"
      onClick={onClick}
    />
  );
};

// ---------------------------------------------------------------------------

const FullscreenPreview = ({
  ids,
  initialIndex,
  onClose,
}: {
  ids: string[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const { blobUrl, isLoading } = useAuthenticatedFileBlob(
    ids[currentIndex],
    "original",
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev + 1) % ids.length);
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev - 1 + ids.length) % ids.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, ids.length]);

  return createPortal(
    <div className="GridInlineThumbnailCell">
      <div className="fullscreen-overlay" onClick={onClose}>
        <button
          className="fullscreen-nav fullscreen-nav-left"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev - 1 + ids.length) % ids.length);
          }}
        >
          <Icon icon="angle-left" size="3x" />
        </button>

        <div
          className="fullscreen-content"
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <Loader size="lg" />
          ) : (
            <>
              {blobUrl ? (
                <img src={blobUrl} alt="" className="fullscreen-image" />
              ) : (
                <div className="fullscreen-no-preview">
                  <Icon icon="image" size="4x" />
                  <span>This image could not be loaded</span>
                </div>
              )}
              {ids.length > 1 && (
                <div className="fullscreen-caption">
                  {currentIndex + 1} / {ids.length}
                </div>
              )}
            </>
          )}
        </div>

        <button
          className="fullscreen-nav fullscreen-nav-right"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev + 1) % ids.length);
          }}
        >
          <Icon icon="angle-right" size="3x" />
        </button>

        <button className="fullscreen-close" onClick={onClose}>
          <Icon icon="close" size="2x" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------

export const GridInlineThumbnailCell = (params: ICellRendererParams) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  const value = params.value as string | null | undefined;
  if (!value) return null;

  const ids = value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  if (ids.length === 0) return null;

  return (
    <div className="grid-thumbnail-strip">
      {fullscreenIndex !== null && (
        <FullscreenPreview
          ids={ids}
          initialIndex={fullscreenIndex}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
      {ids.map((id, index) => (
        <ThumbnailImage
          key={id}
          id={id}
          onClick={() => setFullscreenIndex(index)}
        />
      ))}
    </div>
  );
};
